import { useEffect, useRef, useState } from "react";

/**
 * VideoTile — autoplays even on stubborn browsers.
 * Forces .play() on mount + when loaded + when visible.
 * Shows a small overlay if autoplay fails.
 */
export default function VideoTile({
  src,
  poster,
  className = "",
  loop = true,
  muted = true,
  ...rest
}) {
  const ref = useRef(null);
  const [needsTap, setNeedsTap] = useState(false);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.muted = true;
    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.then === "function") {
        p.catch(() => setNeedsTap(true));
      }
    };
    // First attempt
    tryPlay();
    // Retry when data loads
    v.addEventListener("loadeddata", tryPlay);
    v.addEventListener("canplay", tryPlay);
    // Retry when scrolled into view
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) tryPlay();
        });
      },
      { threshold: 0.1 }
    );
    io.observe(v);
    return () => {
      io.disconnect();
      v.removeEventListener("loadeddata", tryPlay);
      v.removeEventListener("canplay", tryPlay);
    };
  }, []);

  const handleManualPlay = () => {
    const v = ref.current;
    if (v) {
      v.muted = true;
      v.play().then(() => setNeedsTap(false)).catch(() => {});
    }
  };

  return (
    <>
      <video
        ref={ref}
        src={src}
        poster={poster}
        autoPlay
        muted={muted}
        loop={loop}
        playsInline
        preload="auto"
        className={className}
        {...rest}
      />
      {needsTap && (
        <button
          onClick={handleManualPlay}
          aria-label="Відтворити відео"
          className="absolute inset-0 z-10 flex items-center justify-center bg-black/30 backdrop-blur-[2px] cursor-pointer"
        >
          <span className="w-14 h-14 rounded-full bg-white/15 backdrop-blur-md border border-white/30 flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-6 h-6 text-white ml-1" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
        </button>
      )}
    </>
  );
}
